import React from 'react';
import { Sparkles, Zap, ShieldCheck, Users } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const AboutPage: React.FC = () => {
  const { t } = useLanguage();
  
  return (
    <div className="pt-24 pb-16">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto mb-12 text-center">
          <h1 className="text-4xl font-bold mb-4">
            {t('about.title')}
          </h1>
          <p className="text-xl text-gray-400">
            {t('about.subtitle')}
          </p>
        </div>
        
        {/* Mission Section */}
        <div className="max-w-3xl mx-auto mb-16">
          <h2 className="text-2xl font-bold mb-6">{t('about.mission.title')}</h2>
          <p className="text-lg text-gray-300 mb-4">
            {t('about.mission.paragraph1')}
          </p>
          <p className="text-lg text-gray-300">
            {t('about.mission.paragraph2')}
          </p>
        </div>
        
        {/* Values Section */}
        <div className="max-w-5xl mx-auto mb-16">
          <h2 className="text-2xl font-bold mb-8 text-center">{t('about.values.title')}</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-gray-800 p-6 rounded-xl border border-gray-700">
              <div className="flex items-start gap-4">
                <div className="bg-purple-500/20 p-3 rounded-lg">
                  <Sparkles className="h-6 w-6 text-purple-500" />
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2">{t('about.values.creativity.title')}</h3>
                  <p className="text-gray-300">
                    {t('about.values.creativity.description')}
                  </p>
                </div>
              </div>
            </div>
            
            <div className="bg-gray-800 p-6 rounded-xl border border-gray-700">
              <div className="flex items-start gap-4">
                <div className="bg-pink-500/20 p-3 rounded-lg">
                  <Zap className="h-6 w-6 text-pink-500" />
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2">{t('about.values.innovation.title')}</h3>
                  <p className="text-gray-300">
                    {t('about.values.innovation.description')}
                  </p>
                </div>
              </div>
            </div>
            
            <div className="bg-gray-800 p-6 rounded-xl border border-gray-700">
              <div className="flex items-start gap-4">
                <div className="bg-purple-500/20 p-3 rounded-lg">
                  <ShieldCheck className="h-6 w-6 text-purple-500" />
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2">{t('about.values.accessibility.title')}</h3>
                  <p className="text-gray-300">
                    {t('about.values.accessibility.description')}
                  </p>
                </div>
              </div>
            </div>
            
            <div className="bg-gray-800 p-6 rounded-xl border border-gray-700">
              <div className="flex items-start gap-4">
                <div className="bg-pink-500/20 p-3 rounded-lg">
                  <Users className="h-6 w-6 text-pink-500" />
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2">{t('about.values.community.title')}</h3>
                  <p className="text-gray-300">
                    {t('about.values.community.description')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* How It Works Section */}
        <div className="max-w-3xl mx-auto mb-16">
          <h2 className="text-2xl font-bold mb-6">{t('about.howItWorks.title')}</h2>
          
          <div className="space-y-8">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 bg-gray-800 w-10 h-10 rounded-full flex items-center justify-center border border-gray-700">
                <span className="text-lg font-bold">1</span>
              </div>
              <div>
                <h3 className="text-xl font-bold mb-2">{t('about.howItWorks.step1.title')}</h3>
                <p className="text-gray-300">
                  {t('about.howItWorks.step1.description')}
                </p>
              </div>
            </div>
            
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 bg-gray-800 w-10 h-10 rounded-full flex items-center justify-center border border-gray-700">
                <span className="text-lg font-bold">2</span>
              </div>
              <div>
                <h3 className="text-xl font-bold mb-2">{t('about.howItWorks.step2.title')}</h3>
                <p className="text-gray-300">
                  {t('about.howItWorks.step2.description')}
                </p>
              </div>
            </div>
            
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 bg-gray-800 w-10 h-10 rounded-full flex items-center justify-center border border-gray-700">
                <span className="text-lg font-bold">3</span>
              </div>
              <div>
                <h3 className="text-xl font-bold mb-2">{t('about.howItWorks.step3.title')}</h3>
                <p className="text-gray-300">
                  {t('about.howItWorks.step3.description')}
                </p>
              </div>
            </div>
            
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 bg-gray-800 w-10 h-10 rounded-full flex items-center justify-center border border-gray-700">
                <span className="text-lg font-bold">4</span>
              </div>
              <div>
                <h3 className="text-xl font-bold mb-2">{t('about.howItWorks.step4.title')}</h3>
                <p className="text-gray-300">
                  {t('about.howItWorks.step4.description')}
                </p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Contact Section */}
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl font-bold mb-6">{t('about.contact.title')}</h2>
          <p className="text-lg text-gray-300 mb-8">
            {t('about.contact.subtitle')}
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-gray-800 p-6 rounded-xl border border-gray-700">
              <h3 className="text-xl font-bold mb-4">{t('about.contact.info.title')}</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-2">
                  <span className="font-medium text-gray-400">{t('about.contact.info.email')}</span>
                  <span className="text-gray-300">info@tattoonailgenius.com</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-medium text-gray-400">{t('about.contact.info.support')}</span>
                  <span className="text-gray-300">support@tattoonailgenius.com</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-medium text-gray-400">{t('about.contact.info.business')}</span>
                  <span className="text-gray-300">business@tattoonailgenius.com</span>
                </li>
              </ul>
            </div>
            
            <div className="bg-gray-800 p-6 rounded-xl border border-gray-700">
              <h3 className="text-xl font-bold mb-4">{t('about.contact.artists.title')}</h3>
              <p className="text-gray-300 mb-4">
                {t('about.contact.artists.description')}
              </p>
              <a 
                href="#" 
                className="inline-block bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white px-6 py-2 rounded-lg font-medium transition-all"
              >
                {t('about.contact.artists.cta')}
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutPage;